import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useVideoRingSettings } from '@/hooks/useVideoRingSettings';
import { useAppUsers } from '@/hooks/useAppUsers';
import { Loader2 } from 'lucide-react';
import { MultiSelect } from '@/components/ui/multi-select';

function VideoRingLevelsForm({ businessUnit }: { businessUnit: 'br' | 'us' }) {
  const { settings, isLoading, updateSettings, isUpdating } = useVideoRingSettings(businessUnit);
  const { users } = useAppUsers();

  const [level1, setLevel1] = useState({ name: 'First Responders', timeout_seconds: 30, user_ids: [] as string[] });
  const [level2, setLevel2] = useState({ name: 'Management', timeout_seconds: 30, user_ids: [] as string[] });
  const [level3, setLevel3] = useState({ name: 'Final Escalation', timeout_seconds: 30, user_ids: [] as string[] });

  useEffect(() => {
    if (settings) {
      const l1 = settings.find((s) => s.level === 1);
      const l2 = settings.find((s) => s.level === 2);
      const l3 = settings.find((s) => s.level === 3);

      if (l1) setLevel1({ name: l1.name, timeout_seconds: l1.timeout_seconds, user_ids: l1.user_ids });
      if (l2) setLevel2({ name: l2.name, timeout_seconds: l2.timeout_seconds, user_ids: l2.user_ids });
      if (l3) setLevel3({ name: l3.name, timeout_seconds: l3.timeout_seconds, user_ids: l3.user_ids });
    }
  }, [settings]);

  const handleSave = () => {
    updateSettings([
      { level: 1, ...level1 },
      { level: 2, ...level2 },
      { level: 3, ...level3 },
    ]);
  };

  // Filter users by business_unit array field
  const userOptions = useMemo(() => {
    return (users || [])
      .filter((user: any) => {
        if (!user.active) return false;
        const buArray = user.business_units as string[] | null;
        if (!buArray || buArray.length === 0) return true;
        if (buArray.includes(businessUnit)) return true;
        return (user as any).primary_business_unit === businessUnit;
      })
      .map((user) => ({
        value: user.id,
        label: user.display_name || `${user.first_name} ${user.last_name}`,
      }));
  }, [users, businessUnit]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Level 1 */}
      <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
        <Label className="text-lg font-semibold">Level 1 - {level1.name}</Label>
        <div className="space-y-2">
          <Label>Level Name</Label>
          <Input value={level1.name} onChange={(e) => setLevel1({ ...level1, name: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Users</Label>
          <MultiSelect options={userOptions} value={level1.user_ids} onChange={(values) => setLevel1({ ...level1, user_ids: values })} placeholder="Select users for Level 1" />
        </div>
        <div className="space-y-2">
          <Label>Timeout (seconds)</Label>
          <Input type="number" min="10" max="120" value={level1.timeout_seconds} onChange={(e) => setLevel1({ ...level1, timeout_seconds: parseInt(e.target.value) || 30 })} />
        </div>
      </div>

      {/* Level 2 */}
      <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
        <Label className="text-lg font-semibold">Level 2 - {level2.name}</Label>
        <div className="space-y-2">
          <Label>Level Name</Label>
          <Input value={level2.name} onChange={(e) => setLevel2({ ...level2, name: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Users</Label>
          <MultiSelect options={userOptions} value={level2.user_ids} onChange={(values) => setLevel2({ ...level2, user_ids: values })} placeholder="Select users for Level 2" />
        </div>
        <div className="space-y-2">
          <Label>Timeout (seconds)</Label>
          <Input type="number" min="10" max="120" value={level2.timeout_seconds} onChange={(e) => setLevel2({ ...level2, timeout_seconds: parseInt(e.target.value) || 30 })} />
        </div>
      </div>

      {/* Level 3 */}
      <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
        <Label className="text-lg font-semibold">Level 3 - {level3.name}</Label>
        <div className="space-y-2">
          <Label>Level Name</Label>
          <Input value={level3.name} onChange={(e) => setLevel3({ ...level3, name: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Users</Label>
          <MultiSelect options={userOptions} value={level3.user_ids} onChange={(values) => setLevel3({ ...level3, user_ids: values })} placeholder="Select users for Level 3" />
        </div>
        <div className="space-y-2">
          <Label>Timeout (seconds)</Label>
          <Input type="number" min="10" max="120" value={level3.timeout_seconds} onChange={(e) => setLevel3({ ...level3, timeout_seconds: parseInt(e.target.value) || 30 })} />
        </div>
      </div>

      <Button onClick={handleSave} disabled={isUpdating} className="w-full">
        {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Save Configuration
      </Button>
    </div>
  );
}

export function VideoRingSettingsPanel() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Video Call Ring System</CardTitle>
        <CardDescription>
          Configure automatic escalation for immediate video calls. Each level will be attempted for the specified timeout before escalating to the next level.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="br">
          <TabsList className="mb-4">
            <TabsTrigger value="br">🇧🇷 Brasil</TabsTrigger>
            <TabsTrigger value="us">🇺🇸 EUA</TabsTrigger>
          </TabsList>
          <TabsContent value="br">
            <VideoRingLevelsForm businessUnit="br" />
          </TabsContent>
          <TabsContent value="us">
            <VideoRingLevelsForm businessUnit="us" />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
